import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, GraduationCap, Users, Star, Globe } from "lucide-react";
import { SectionHeader } from "@/components/SectionComponents";

const teacherTypes = [
  { icon: GraduationCap, title: "Certified Professionals",  description: "Credentialed language teachers with formal qualifications, specialized in corporate and academic contexts." },
  { icon: Globe,         title: "Native Speaker Coaches",   description: "Native speakers passionate about their language and culture, ideal for conversation practice and cultural immersion." },
  { icon: Users,         title: "Group Facilitators",       description: "Experienced facilitators who run structured group sessions for corporate teams and student cohorts." },
];

const sampleTeachers = [
  { name: "Yuki Tanaka",      lang: "Japanese → English",  sessions: "412 sessions", rating: 4.9 },
  { name: "Carlos Méndez",    lang: "Spanish → German",   sessions: "287 sessions", rating: 4.8 },
  { name: "Fatima Al-Hassan", lang: "Arabic → French",    sessions: "355 sessions", rating: 5.0 },
  { name: "Elena Petrova",    lang: "Russian → English",  sessions: "198 sessions", rating: 4.9 },
  { name: "Liu Wei",          lang: "Mandarin → Spanish", sessions: "502 sessions", rating: 4.8 },
  { name: "Amara Diallo",     lang: "French → English",   sessions: "163 sessions", rating: 4.7 },
];

export default function Teachers() {
  return (
    <div className="min-h-screen bg-background">

      <section className="pt-28 pb-16 bg-surface border-b border-border">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">Language Teachers</p>
          <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground leading-tight mb-5">
            Learn from the World's Best Language Teachers
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed mb-8">
            Certified professionals, native speaker coaches, and group facilitators. One-on-one or group sessions, online or in-person.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
              <Link to="/contact">Find a Teacher <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="transition-fast">
              <Link to="/contact">Become a Teacher</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Teacher types */}
      <section className="py-24 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Teaching Formats" title="Three Types of Language Instruction" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {teacherTypes.map((t) => (
              <div key={t.title} className="flex flex-col p-7 rounded-xl border border-border bg-card card-hover">
                <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center mb-5">
                  <t.icon className="w-5 h-5 text-primary" strokeWidth={1.75} />
                </div>
                <h3 className="text-base font-display font-bold text-foreground mb-2">{t.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-5">{t.description}</p>
                <Button asChild variant="outline" size="sm" className="mt-auto border-primary text-primary hover:bg-primary/5 transition-fast">
                  <Link to="/contact">Get Started</Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample teachers SECCIÓN COMENTADA TEMPORALMENTE
      <section className="py-20 bg-surface">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Our Community" title="Meet Some of Our Teachers" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sampleTeachers.map((t) => (
              <Card key={t.name} className="p-4 border border-border shadow-sm-token card-hover">
                <CardContent className="p-0 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-primary">{t.name[0]}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-display font-bold text-foreground">{t.name}</div>
                    <div className="text-xs text-primary font-medium mt-0.5">{t.lang}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <Star className="w-3 h-3 fill-primary text-primary" />
                      <span className="text-xs font-semibold text-foreground">{t.rating}</span>
                      <span className="text-xs text-muted-foreground">{t.sessions}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
              <Link to="/contact">Browse All Teachers <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
      */}

      {/* For teachers */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-display font-bold text-primary-foreground mb-3">Are You a Language Teacher?</h2>
          <p className="text-base text-primary-foreground/75 leading-relaxed mb-8 max-w-lg mx-auto">
            Join thousands of language educators on Lingua2. Build your student base, set your own rates, and teach on your schedule.
          </p>
          <Button asChild size="lg" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-semibold transition-fast">
            <Link to="/contact">Apply as a Teacher <ArrowRight className="ml-2 w-4 h-4" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}